import React, { useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { RadioButton } from "primereact/radiobutton";

import { decideEichrecht } from "../../../../services/onboarding/eichrecht";
import { EichrechtVerdict, Prospect } from "../../../../types/onboarding";

const MAX_NOTE = 2000;

interface EichrechtDecisionDialogProps {
  manufacturer: string;
  model: string;
  // What the customer entered for it, when there is one customer to show (CWA-229).
  details?: string | null;
  visible: boolean;
  onHide: () => void;
  // Re-reads the prospect once the decision is saved.
  onDecided: () => Promise<unknown> | void;
}

// What the customer entered for the unlisted wallbox (CWA-229), e.g. "11 kW · AC · Fixed cable".
export const specsText = (p: Prospect): string =>
  [
    p.wallbox_power_kw != null ? `${p.wallbox_power_kw} kW` : null,
    p.wallbox_current_type,
    p.wallbox_connection_type === "CABLE" ? "Fixed cable" : p.wallbox_connection_type === "SOCKET" ? "Socket" : null,
  ]
    .filter(Boolean)
    .join(" · ");

/**
 * Support's Eichrecht check of an unlisted wallbox (CWA-230). The decision is
 * saved for the wallbox model, so the next customer with it doesn't wait; a
 * not-conform verdict blocks nobody but shows a warning on every such prospect.
 */
const EichrechtDecisionDialog: React.FC<EichrechtDecisionDialogProps> = ({
  manufacturer,
  model,
  details,
  visible,
  onHide,
  onDecided,
}) => {
  const [verdict, setVerdict] = useState<EichrechtVerdict | null>(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmed = note.trim();
  const canSave = verdict !== null && trimmed.length > 0 && trimmed.length <= MAX_NOTE && !saving;

  const save = async () => {
    if (!verdict) return;
    setSaving(true);
    setError(null);
    try {
      await decideEichrecht({ manufacturer, model, verdict, note: trimmed });
      await onDecided();
      onHide();
    } catch {
      setError("The decision could not be saved. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button label="Cancel" severity="secondary" outlined disabled={saving} onClick={onHide} />
      <Button label="Save decision" icon="pi pi-check" loading={saving} disabled={!canSave} onClick={save} />
    </div>
  );

  return (
    <Dialog header="Eichrecht check" visible={visible} onHide={onHide} footer={footer} style={{ width: "36rem" }} breakpoints={{ "960px": "90vw" }}>
      {error && <Message severity="error" className="w-full mb-3" text={error} />}
      <p className="mt-0 mb-1 font-semibold">{`${manufacturer} ${model}`}</p>
      {details && <p className="mt-0 text-600">{details}</p>}
      <p className="text-600 text-sm">
        The decision is saved for this wallbox model and applies to every customer with it. Not conform blocks nobody; each
        such prospect shows a warning.
      </p>

      <div className="flex gap-4 mb-3" role="radiogroup" aria-label="Verdict">
        <div className="flex align-items-center gap-2">
          <RadioButton inputId="eichrecht-conform" checked={verdict === "CONFORM"} onChange={() => setVerdict("CONFORM")} />
          <label htmlFor="eichrecht-conform">Eichrechtskonform</label>
        </div>
        <div className="flex align-items-center gap-2">
          <RadioButton inputId="eichrecht-not-conform" checked={verdict === "NOT_CONFORM"} onChange={() => setVerdict("NOT_CONFORM")} />
          <label htmlFor="eichrecht-not-conform">Not eichrechtskonform</label>
        </div>
      </div>

      <label htmlFor="eichrecht-note" className="block mb-2">
        Note (evidence)
      </label>
      <InputTextarea id="eichrecht-note" className="w-full" rows={4} value={note} onChange={(e) => setNote(e.target.value)} />
      <div className={`text-right text-sm ${trimmed.length > MAX_NOTE ? "text-red-500" : "text-500"}`}>
        {trimmed.length} / {MAX_NOTE}
      </div>
    </Dialog>
  );
};

export default EichrechtDecisionDialog;
